import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FirebaseAuthContext } from 'context/FirebaseAuthContext.js';
import PageHeader from 'components/common/PageHeader';
import { Card, Row, Col, Table, Badge, Spinner, Button, Form, Alert, Pagination, Tab, Tabs } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPlay,
  faTimes,
  faSync,
  faCheckCircle,
  faSpinner,
  faExclamationTriangle,
  faEye,
  faListUl,
  faClock,
  faCalendarAlt,
  faFileExport
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

import { db } from 'config/firebase.js';
import { collection, query, orderBy, onSnapshot, limit, where, doc, updateDoc } from 'firebase/firestore';
import BatchProgressCard, { BATCH_STATUS, STATUS_CONFIG } from './BatchProgressCard';
import ImportFieldSelector from './ImportFieldSelector';
import BatchJobQueue from './BatchJobQueue';
import BatchScheduleModal from './BatchScheduleModal';
import BatchTemplateManager from './BatchTemplateManager';
import BatchExportModal from './BatchExportModal';

const CLOUD_FUNCTIONS_URL =
  process.env.REACT_APP_CLOUD_FUNCTIONS_URL || 'https://us-central1-stk-merlin.cloudfunctions.net';

const BatchJobManager = () => {
  const navigate = useNavigate();
  const { user, authLoading } = useContext(FirebaseAuthContext);

  // State
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeBatch, setActiveBatch] = useState(null);
  const [batchItems, setBatchItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [processingBatchId, setProcessingBatchId] = useState(null);

  // Create batch form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newBatchName, setNewBatchName] = useState('');
  const [skuInput, setSkuInput] = useState('');
  const [forceRediscovery, setForceRediscovery] = useState(false);
  const [importDefaults, setImportDefaults] = useState({
    images: true,
    amazonTitle: false,
    brand: true,
    packageQuantity: true,
    salesRank: true
  });
  const [creating, setCreating] = useState(false);

  // Scheduling and template state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [currentFilters, setCurrentFilters] = useState({});

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportBatch, setExportBatch] = useState(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (authLoading) return;
    if (!user) return navigate('/');
  }, [authLoading, user, navigate]);

  // Real-time listener for batches
  useEffect(() => {
    const constraints = [orderBy('createdAt', 'desc'), limit(50)];
    if (statusFilter !== 'all') {
      constraints.unshift(where('status', '==', statusFilter));
    }

    const batchesQuery = query(
      collection(db, 'productCatalog/topicz/asinDiscoveryBatches'),
      ...constraints
    );

    const unsubscribe = onSnapshot(
      batchesQuery,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setBatches(data);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching batches:', error);
        toast.error('Error loading batches');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [statusFilter]);

  // Fetch batch items when active batch changes
  useEffect(() => {
    if (!activeBatch) {
      setBatchItems([]);
      return;
    }

    setItemsLoading(true);

    const itemsQuery = query(
      collection(db, 'productCatalog/topicz/asinDiscoveryBatchItems'),
      where('batchId', '==', activeBatch),
      orderBy('sku'),
      limit(100)
    );

    const unsubscribe = onSnapshot(
      itemsQuery,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setBatchItems(data);
        setItemsLoading(false);
      },
      (error) => {
        console.error('Error fetching batch items:', error);
        setItemsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [activeBatch]);

  // Parse SKUs from textarea
  const parseSkus = useCallback(() => {
    return skuInput
      .split(/[\n,;]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0);
  }, [skuInput]);

  // Create new batch
  const handleCreateBatch = async () => {
    const skus = parseSkus();
    if (skus.length === 0) {
      toast.error('Please enter at least one SKU');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${CLOUD_FUNCTIONS_URL}/asinBatchCreate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          skus,
          config: {
            name: newBatchName || undefined,
            forceRediscovery,
            importDefaults
          }
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to create batch');
      }

      toast.success(`Batch created with ${skus.length} SKUs`, { theme: 'colored' });
      setShowCreateForm(false);
      setNewBatchName('');
      setSkuInput('');
      setActiveBatch(data.batch.id);

    } catch (error) {
      console.error('Error creating batch:', error);
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  // Process a batch
  const handleProcessBatch = async (batchId, continuationToken = null) => {
    setProcessingBatchId(batchId);
    try {
      const response = await fetch(`${CLOUD_FUNCTIONS_URL}/asinBatchProcess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batchId, continuationToken })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Processing failed');
      }

      if (data.continuationToken) {
        // More items to process - auto-continue
        toast.info(`Processed chunk, continuing... (${data.processed} items)`, { autoClose: 2000 });
        // Recursive call for continuation
        await handleProcessBatch(batchId, data.continuationToken);
      } else {
        toast.success(`Batch processing complete! ${data.processed} items processed`, { theme: 'colored' });
      }

    } catch (error) {
      console.error('Error processing batch:', error);
      toast.error(error.message);
    } finally {
      setProcessingBatchId(null);
    }
  };

  // Cancel a batch
  const handleCancelBatch = async (batchId) => {
    try {
      const response = await fetch(`${CLOUD_FUNCTIONS_URL}/asinBatchCancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batchId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Cancel failed');
      }

      toast.success('Batch cancelled', { theme: 'colored' });

    } catch (error) {
      console.error('Error cancelling batch:', error);
      toast.error(error.message);
    }
  };

  // Retry a failed/partial/cancelled batch
  const handleRetryBatch = async (batchId) => {
    // Reset batch status to pending so it can be processed again
    try {
      // First, reset the batch status via Firestore update
      const batchRef = doc(db, 'productCatalog/topicz/asinDiscoveryBatches', batchId);
      await updateDoc(batchRef, {
        status: 'pending',
        completedAt: null
      });

      toast.info('Batch reset to pending, starting processing...', { autoClose: 2000 });

      // Then start processing
      await handleProcessBatch(batchId);

    } catch (error) {
      console.error('Error retrying batch:', error);
      toast.error(error.message);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return (
      <Badge bg={config.variant}>
        <FontAwesomeIcon icon={config.icon} spin={config.spin} className="me-1" />
        {config.label}
      </Badge>
    );
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleString();
  };

  // Get item status badge
  const getItemStatusBadge = (status) => {
    const config = {
      pending: { bg: 'secondary', label: 'Pending' },
      processing: { bg: 'primary', label: 'Processing' },
      completed: { bg: 'success', label: 'Completed' },
      failed: { bg: 'danger', label: 'Failed' },
      skipped: { bg: 'light', textColor: 'dark', label: 'Skipped' }
    };
    const s = config[status] || config.pending;
    return <Badge bg={s.bg} text={s.textColor}>{s.label}</Badge>;
  };

  if (authLoading || loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  const skuCount = parseSkus().length;

  return (
    <>
      <PageHeader title="ASIN Batch Discovery" className="mb-3">
        <p className="mb-0 fs--1 text-600">
          Manage batch ASIN discovery jobs for bulk product matching
        </p>
      </PageHeader>

      <Row>
        {/* Left Column - Job Queue and Templates */}
        <Col lg={6}>
          {/* Create Form Card */}
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <FontAwesomeIcon icon={faListUl} className="me-2" />
                Create Batch
              </span>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowScheduleModal(true)}
                >
                  <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                  Schedule
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowCreateForm(!showCreateForm)}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  {showCreateForm ? 'Hide' : 'New Batch'}
                </Button>
              </div>
            </Card.Header>

            {/* Create Form */}
            {showCreateForm && (
              <Card.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Batch Name (optional)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="ASIN Discovery - January 2026"
                      value={newBatchName}
                      onChange={(e) => setNewBatchName(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      SKUs
                      {skuCount > 0 && (
                        <Badge bg="primary" className="ms-2">{skuCount} SKUs</Badge>
                      )}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      placeholder="Enter SKUs (one per line, or comma/semicolon separated)"
                      value={skuInput}
                      onChange={(e) => setSkuInput(e.target.value)}
                    />
                    <Form.Text className="text-muted">
                      Maximum 5,000 SKUs per batch
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="forceRediscovery"
                      label="Force re-discovery (ignore existing verifications)"
                      checked={forceRediscovery}
                      onChange={(e) => setForceRediscovery(e.target.checked)}
                    />
                  </Form.Group>

                  <ImportFieldSelector
                    preferences={importDefaults}
                    onChange={setImportDefaults}
                    showDefaults
                  />

                  <div className="d-flex gap-2 mt-3">
                    <Button
                      variant="success"
                      onClick={handleCreateBatch}
                      disabled={creating || skuCount === 0}
                    >
                      {creating ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faPlus} className="me-1" />
                          Create Batch
                        </>
                      )}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setShowCreateForm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            )}
          </Card>

          {/* Job Queue with Tabs */}
          <div className="mb-3">
            <BatchJobQueue
              onSelectJob={(job) => setActiveBatch(job.activeBatchId || job.id)}
              onProcessJob={(job) => handleProcessBatch(job.activeBatchId || job.id)}
              onCancelJob={(job) => handleCancelBatch(job.activeBatchId || job.id)}
              selectedJobId={activeBatch}
            />
          </div>

          {/* Templates */}
          <BatchTemplateManager
            currentFilters={currentFilters}
            onSelectTemplate={(template) => {
              // Apply template filters
              setCurrentFilters(template.filterCriteria || {});
              toast.info(`Applied filters from "${template.name}"`);
            }}
            onRunTemplate={(template) => {
              setSelectedTemplate(template);
              setShowScheduleModal(true);
            }}
          />
        </Col>

        {/* Right Column - Batch Details */}
        <Col lg={6}>
          {activeBatch ? (
            <>
              {/* Progress Card */}
              <BatchProgressCard
                batchId={activeBatch}
                onProcess={handleProcessBatch}
                onCancel={handleCancelBatch}
                onRetry={handleRetryBatch}
              />

              {/* Export Button */}
              {batches.find(b => b.id === activeBatch) && (
                <div className="d-flex gap-2 mb-3">
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      const batch = batches.find(b => b.id === activeBatch);
                      setExportBatch(batch);
                      setShowExportModal(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faFileExport} className="me-1" />
                    Export Results
                  </Button>
                </div>
              )}

              {/* Batch Items */}
              <Card>
                <Card.Header>
                  <FontAwesomeIcon icon={faListUl} className="me-2" />
                  Batch Items
                </Card.Header>
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {itemsLoading ? (
                    <Card.Body className="text-center py-4">
                      <Spinner animation="border" size="sm" />
                    </Card.Body>
                  ) : batchItems.length === 0 ? (
                    <Card.Body className="text-center text-muted py-4">
                      No items in this batch
                    </Card.Body>
                  ) : (
                    <Table size="sm" hover className="mb-0">
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Status</th>
                          <th>ASIN</th>
                          <th>Confidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batchItems.map((item) => (
                          <tr key={item.id}>
                            <td><code className="small">{item.sku}</code></td>
                            <td>{getItemStatusBadge(item.status)}</td>
                            <td>
                              {item.result?.asin ? (
                                <a
                                  href={`https://www.amazon.com/dp/${item.result.asin}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="small"
                                >
                                  {item.result.asin}
                                </a>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {item.result?.confidence ? (
                                <Badge bg={item.result.confidence >= 0.9 ? 'success' : item.result.confidence >= 0.7 ? 'warning' : 'danger'}>
                                  {Math.round(item.result.confidence * 100)}%
                                </Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
                {batchItems.length > 0 && (
                  <Card.Footer className="text-end small text-muted">
                    Showing first 100 items
                  </Card.Footer>
                )}
              </Card>
            </>
          ) : (
            <Card>
              <Card.Body className="text-center text-muted py-5">
                <FontAwesomeIcon icon={faEye} size="2x" className="mb-3" />
                <p>Select a batch to view details</p>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Schedule Modal */}
      <BatchScheduleModal
        show={showScheduleModal}
        onHide={() => {
          setShowScheduleModal(false);
          setSelectedTemplate(null);
        }}
        filterCriteria={selectedTemplate?.filterCriteria || currentFilters}
        jobConfig={{
          name: selectedTemplate?.name || newBatchName || 'ASIN Discovery',
          forceRediscovery,
          importDefaults
        }}
        userId={user?.uid}
        onScheduled={(job) => {
          setActiveBatch(job.activeBatchId || job.id);
        }}
      />

      {/* Export Modal */}
      <BatchExportModal
        show={showExportModal}
        onHide={() => {
          setShowExportModal(false);
          setExportBatch(null);
        }}
        batch={exportBatch}
      />
    </>
  );
};

export default BatchJobManager;
